import { Sortable } from 'gf-types';
import { CheckboxShapes, TableMetadata } from './basic-elements-types';
import { DataSourceOrigin } from '../helpers/constants';
import { CriteriaType, ErrorType, InputSelectionType } from '../templateeditor/components/FieldValidations/constants';
import { SupportedFileTypes } from '../templateeditor/templateeditor-types';

// These types should mirror the types in GoFormz.Common.Contracts.Template

export type Template = TransientTemplate & {
  actions?: TemplateAction[];
  autoTags?: AutoTag[];
  dataLayouts?: DataLayout[];
  fields?: TemplateField[];
  fileId?: string;
  folderId?: string;
  parentFolderId?: string;
  parentFolderName?: string;
  hideTemplate?: boolean;
  id?: string;
  identifierFieldId?: string;
  isDemo?: boolean;
  lastUpdatedDate?: string;
  name?: string;
  namedFunctions?: NamedFunctionType[];
  pages?: TemplatePage[];
  pdfExportUseVisible?: boolean;
  printConfigs?: PrintConfig[];
  rawDefaultFormName?: string;
  defaultFormName?: string;
  schemaVersion?: number;
  syncLevel?: number;
  unpublishedChanges?: boolean;
  versionId?: string;
  versionNumber?: number;
  requiredBackgroundColor?: string;
  defaultFieldDisplaySettings?: DefaultFieldDisplaySettings;
};

/** These are the state props that are not persisted to the data store */
export type TransientTemplate = {
  isNew?: boolean;
  fileExtension?: SupportedFileTypes;
  aiMetrics?: AiMetrics;
};

export type AiMetrics = {
  /** The number of time the template was saved after analysis was run. */
  saveCount: number;
  /** The number of times the analyze button was pressed for this template. */
  analyzeCount: number;
  /** The field level changes that are tracked. */
  fieldMetrics: { [FieldId: string]: AiFieldMetrics };
};

/**
 * Tracks the state of AI field metric changes
 */
export enum AiFieldMetricState {
  /** No change */
  NoChange = 0,
  /** Changed, but not tracked at save yet */
  Changed = 1,
  /** Already tracked at template save, skip */
  Tracked = -1
}

export type AiFieldMetrics = {
  moved?: AiFieldMetricState;
  renamed?: AiFieldMetricState;
  typeChanged?: AiFieldMetricState;
  resized?: AiFieldMetricState;
  tableColumnAdded?: AiFieldMetricState;
  tableColumnDeleted?: AiFieldMetricState;
  tableColumnResized?: AiFieldMetricState;
  tableRowCountChanged?: AiFieldMetricState;
  tableRowResized?: AiFieldMetricState;
  deleted?: AiFieldMetricState;
};

export type DefaultFieldDisplaySettings = {
  align: string;
  backgroundColor: string;
  color: string;
  focusColor: string;
  fontName: string;
  fontSize: number;
};

export type TemplateVersion = {
  id: string;
  templateId: string;
  versionNumber: number;
  note: string;
  noteId?: string;
  createdDate: string;
  DeletedDate?: string;
};

export type TemplateField = TransientTemplateField & {
  id: string;
  name: string;
  description?: string;
  //TO-DO: Consider moving this property out of this state
  //Determine whether the user has commited to the field name by clicking out of the input field or renaming
  isFieldCommitted?: boolean;
  /** One of the FieldTypes from [FieldTypes](../helpers/constants.ts) */
  type?: number;
  align?: string;
  color?: string;
  columns?: TemplateColumn[];
  defaultValue?: string;
  filter?: string;
  fontName?: string;
  fontSize?: number;
  formatting?: string;
  formulas?: TemplateFormula[];
  rowCount?: number;
  shared?: boolean;
  signingMethod?: number;
  syncToMobile?: boolean;
  allowOther?: boolean;
  allowOverride?: boolean;
  clearOnFieldChange?: boolean;
  requireMapImage?: boolean;
  dateTimeFormat?: string;
  backgroundCropDimensions?: BackgroundCropDimensions;
  allowedFileTypes?: string[];
  items?: ItemCollectionItem[];
  maxFileCount?: number;
  maxFileSize?: number;
  maxLength?: number;
  multiSelect?: boolean;
  props?: { name: string };
  seed?: number;
  itemCollectionTemplateId?: string;
  locked?: boolean;
  authorOnly?: boolean;
  createdBy?: string;
  required?: boolean;
  dataSource?: string;
  dataSourceOrigin?: DataSourceOrigin;
  displayColumn?: string;
  useCurrent?: boolean;
  imageQuality?: string;
  retainImageAsAttachment?: boolean;
  dateStamp?: boolean;
  characterLimit?: number;
  validationSettings?: ValidationSettings[];
};

export type TransientTemplateField = {
  // this is used by ai template builder and doesn't need to be persisted when saved
  // it is used to show the yellow ai sparkle icon next to the field
  id: string;
  isAiPlaced?: boolean;
  aiCheckboxGroupProperties?: {
    groupId: string;
    groupLabel: string;
  };
};

export type FieldValidation = {
  id?: string;
  type?: number;
};

export type TemplateColumn = {
  id?: string;
  name?: string;
  description?: string;
  sortOrder?: number;
  columnNumber?: number;
  type?: number;
  /** @deprecated This should not be used */
  x?: number;
  /** @deprecated This should not be used */
  hidden?: number;
  /** @deprecated This should not be used */
  transparent?: number;
  defaultValues?: string[];
  multiSelect?: boolean;
  items?: ItemCollectionItem[];
  itemCollectionTemplateId?: string;
  formatting?: string;
  /** @deprecated This should not be used */
  align?: string;
  dataSource?: string;
  displayColumn?: string;
  filters?: string[];
  /** @deprecated This should not be used */
  padding?: number;
  /** @deprecated This should not be used */
  maxSize?: number;
  authorOnly?: boolean;
  retainImageAsAttachment?: string;
  imageQuality?: string;
  allowOther?: boolean;
  allowOverride?: boolean;
  /** @deprecated This should not be used */
  displayType?: number;
  keyboard?: string;
  locked?: boolean;
  lockFirstValue?: boolean;
  useCurrent?: boolean;
  clearOnFieldChange?: boolean;
  dateStamp?: boolean;
  requireMapImage?: boolean;
  dateTimeFormat?: string;
  characterLimit?: number;
  signingMethod?: number;
  color?: string;
};

export type DataLayout = {
  id: string;
  dataLayoutTabs: DataLayoutTab[];
};

export type DataLayoutTab = {
  id: string;
  name: string;
  sections?: DataLayoutSection[];
  sortNumber?: number;
  formulas?: TemplateFormula[];
};

export type DataLayoutSection = {
  id: string;
  name?: string;
  description?: string;
  positions?: (Sortable & DataLayoutPosition)[];
  formulas?: TemplateFormula[];
  sortNumber: number;
};

export type PositionBase = {
  fieldId: string;
  id: string;
};

export type DataLayoutPosition = PositionBase & {
  checkBoxGroupItems?: CheckboxGroupItem[];
  formulas?: TemplateFormula[];
  sortNumber?: number;
  borderColor?: string;
  /** This is only used in Send a Form v2 for now. */
  backgroundColor?: string;
};

export type TemplatePage = {
  fieldPositions?: FieldPosition[];
  elements?: BasicElement[];
  formulas?: TemplateFormula[];
  id: string;
  name?: string;
  number?: number;
  fileId?: string;
  backgroundFileId?: string;
  originalPdfFileId?: string;
  backgroundPdfFileId?: string;
  width?: number;
  pdfUrl?: string;
  positions?: FieldPosition[];
  height?: number;
};

export type BasicElement = {
  angleOfRotation?: number;
  borderRadiusX?: number;
  borderRadiusY?: number;
  columns?: number;
  cropBottom?: number;
  cropLeft?: number;
  cropRight?: number;
  cropTop?: number;
  description?: string;
  fileId?: string;
  fileName?: string;
  fillColor?: string;
  fillOpacity?: number;
  flipX?: boolean;
  flipY?: boolean;
  fontColor?: string;
  fontFamily?: string;
  fontSize?: number;
  fontStyleItalic?: string;
  fontStyleUnderline?: string;
  fontStyleWeight?: number | string;
  height?: number;
  horizontalTextAlign?: string;
  id?: string;
  isLocked?: boolean;
  name?: string;
  radius?: number;
  rows?: number;
  scaleX?: number;
  scaleY?: number;
  shape?: CheckboxShapes;
  skewX?: number;
  skewY?: number;
  strokeColor?: string;
  //Array which specifies the dash pattern by stating intervals via an array.
  //For example, if we pass an array with values [2,3], it means a dash pattern of 2px dash and 3px gap and repeating this pattern infinitely.
  strokeDashArray?: Array<number>;
  strokeDashOffset?: number;
  strokeWidth?: number;
  tableMetadata?: TableMetadata;
  tableRow?: number;
  text?: string;
  type?: number;
  verticalTextAlign?: string;
  width?: number;
  /** Maps to Fabric's Left */
  x?: number;
  /** Maps to Fabric's Top */
  y?: number;
  version?: number;
};

export interface BasicElementSvg {
  svg: string;
  width: number;
  height: number;
}

export type FieldPosition = PositionBase & {
  formulas?: TemplateFormula[];
  itemId?: string;
  tabIndex?: number;
  x: number;
  y: number;
  w: number;
  h: number;
  displayType?: number;
  align?: string;
  originalBackgroundColor?: string;
  backgroundColor?: string;
  color?: string;
  originalFocusColor?: string;
  focusColor?: string;
  fontName?: string;
  fontSize?: number;
  lineHeight?: number;
  maxSize?: number;
  numberOfRows?: number;
  padding?: number;
  pageId?: string;
  pageNumber?: number;
  rowHeight?: number;
  rowNumber?: number;
  rows?: TableRowPosition[];
  columns?: TableColumnPosition[];
  sortNumber?: number;
  startingRow?: number;
  visible?: boolean;
  wrapText?: boolean;
  useDefaultDisplaySettings?: boolean;
  /** True, when this field is selected from the send a form preview, false otherwise. */
  sendAFormSelected?: boolean;
};

export type CheckboxGroupItem = {
  id: string;
  itemId: string;
};

export type TableRowPosition = {
  id?: string;
  rowNumber: number;
  h: number;
};

export type TableColumnPosition = {
  columnId: string;
  x?: number;
  w?: number;
  hidden?: boolean;
  transparent?: boolean;
  align?: string;
  padding?: number;
  maxSize?: number;
  displayType?: number;
  lineHeight?: number;
  wrapText?: boolean;
  schemaVersion?: number;
};

export type ValidationSettingsPosition = {
  id: string;
  validationNumber: number;
};

export interface TemplateSnapCoordinates {
  snapLines: Coordinates;
  offset: Coordinates;
  coordinates: Coordinates;
}

export interface TemplateCoordinates extends Coordinates {
  w?: number;
  h?: number;
}

export interface Coordinates {
  x?: number;
  y?: number;
}

export type TemplateFormula = {
  key: string;
  value: string;
};

export type ValidationSettings = {
  id: string;
  validationNumber: number;
  criteriaType: CriteriaType; // equals, greaterThan, etc
  inputValues: InputSettings[];
  errorSettings: ErrorSettings;
  formulas?: TemplateFormula[];
};

export type ErrorSettings = {
  errorType: ErrorType; // warning, error
  errorMessage: string; // display message
};

export type InputSettings = {
  inputValue: string;
  inputValueSelectionType: InputSelectionType;
};

export type NamedFunctionType = {
  name: string;
  description?: string;
  arguments: NamedFunctionArgument[];
  definition: string;
};

export type NamedFunctionArgument = {
  name: string;
  description?: string;
};

export type BackgroundCropDimensions = {
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  pageNumber?: number;
};

export type TemplateActionMapping = {
  webViewProperty: string;
  templateProperty: string;
};

export type TemplateAction = {
  actionType: number;
  androidUrl?: string;
  id: string;
  iOSUrl?: string;
  mapping: TemplateActionMapping[];
  name: string;
  url: string;
  webUrl?: string;
  windowsUrl?: string;
  workflowId?: string;
  hpMeasureOption?: string;
  hpMeasureFieldId?: string;
};

export type PrintConfig = {
  name: string;
  pages: string;
};

export type AutoTag = {
  id: string;
  formula: string;
};

export type ItemCollection = {
  id: string;
  name: string;
  items?: ItemCollectionItem[];
} & TransientDirty;

export type ItemCollectionItem = {
  id: string;
  displayName?: string;
  value?: string;
  fieldId?: string;
  columnId?: string;
  isDefault?: boolean;
  sortNumber?: number;
};

type TransientDirty = {
  isDirty?: boolean;
};

export type TemplateListResult = {
  items: TemplateListItem[];
  totalCount: number;
};

export type TemplateListItem = {
  name: string;
  published: boolean;
  thumbnailFileId: string;
  currentVersionNumber: number;
  hasDraft: boolean;
  entityType: number;
  id: string;
  lastUpdatedBy: string;
  lastUpdatedDate: string;
  createdBy: string;
  createdDate: string;
  clientId: string;
  folder: string;
  folderId: string;
};

export type TemplateFolder = {
  id: string;
  name: string;
  parentId?: string;
};

export type TemplateFolderList = {
  isFetching?: boolean;
  initialized?: boolean;
  items?: TemplateFolder[];
};

export type TemplateList = {
  count?: number;
  totalCount?: number;
  isFetching?: boolean;
  items?: TemplateListItem[];
  initialized?: boolean;
};

export enum TemplateListEntityTypes {
  Template = 2,
  Folder = 3
}
